import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Search, MapPin, Calendar as CalendarIcon, Clock, Navigation,
    ChevronRight, ChevronLeft, Eye, Video
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { format, addDays, isSameDay, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';

// Fix leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IDF_CENTER = [48.8566, 2.3522];
const IDF_ZOOM = 10;

const HOURS = Array.from({ length: 24 }, (_, i) => {
    const h = i.toString().padStart(2, '0');
    return [{ value: `${h}:00`, label: `${h}:00` }, { value: `${h}:30`, label: `${h}:30` }];
}).flat();

// Custom marker icons
const redIcon = new L.DivIcon({
    className: '',
    html: `<div style="width:28px;height:28px;background:#EF4444;border:3px solid #fff;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,0.3);"></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
});

const blueIcon = new L.DivIcon({
    className: '',
    html: `<div style="width:22px;height:22px;background:#1B365D;border:3px solid #fff;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
});

// Auto-fit map to show all markers (red + blue)
function FitBounds({ points }) {
    const map = useMap();
    useEffect(() => {
        if (!points || points.length === 0) return;
        if (points.length === 1) {
            map.flyTo(points[0], 12, { duration: 1 });
        } else {
            const bounds = L.latLngBounds(points);
            map.flyToBounds(bounds, { padding: [60, 60], duration: 1.2, maxZoom: 14 });
        }
    }, [points, map]);
    return null;
}

// Arrow line from red to blue with distance/duration label next to blue point
function RouteLine({ from, to, routeData }) {
    // Arrow head points near the blue marker
    const angle = Math.atan2(to[0] - from[0], to[1] - from[1]);
    const arrowLen = 0.005;
    const arrowAngle = 0.5;
    const tipLat = to[0] - (to[0] - from[0]) * 0.08;
    const tipLon = to[1] - (to[1] - from[1]) * 0.08;
    const left = [
        tipLat - arrowLen * Math.cos(angle - arrowAngle),
        tipLon - arrowLen * Math.sin(angle - arrowAngle)
    ];
    const right = [
        tipLat - arrowLen * Math.cos(angle + arrowAngle),
        tipLon - arrowLen * Math.sin(angle + arrowAngle)
    ];

    return (
        <>
            {/* Dark dashed line */}
            <Polyline
                positions={[from, to]}
                pathOptions={{ color: '#1B365D', weight: 2.5, dashArray: '10, 6', opacity: 0.7 }}
            />
            {/* Arrow head */}
            <Polyline
                positions={[left, [tipLat, tipLon], right]}
                pathOptions={{ color: '#1B365D', weight: 3, opacity: 0.8 }}
            />
        </>
    );
}

// Distance/duration formatters
const formatDuration = (seconds) => {
    if (!seconds) return '—';
    const m = Math.round(seconds / 60);
    if (m < 60) return `${m} min`;
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return rm > 0 ? `${h}h${rm.toString().padStart(2, '0')}` : `${h}h`;
};
const formatDistance = (meters) => {
    if (!meters) return '—';
    if (meters < 1000) return `${Math.round(meters)} m`;
    return `${(meters / 1000).toFixed(1)} km`;
};

// Haversine fallback for when OSRM is unreachable (corporate network /
// browser ad-blockers / OSRM rate-limit). Distance is straight-line, then
// inflated 1.4x to approximate road distance, plus a 35 km/h average city
// speed for the duration estimate.
function haversineMeters(a, b) {
    const toRad = (d) => (d * Math.PI) / 180;
    const R = 6371000; // m
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lon - a.lon);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const x = Math.sin(dLat / 2) ** 2 + Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
    return 2 * R * Math.asin(Math.sqrt(x));
}

// Fetch driving routes — first try our backend proxy (no CORS / no ad-block
// issues), then fallback to OSRM directly, then Haversine estimate.
async function fetchRoutes(origin, destinations) {
    const results = {};
    if (!origin || !destinations?.length) return results;
    // 1) Backend batch endpoint (preferred)
    try {
        const res = await axios.post(
            `${API_URL}/api/route/batch`,
            {
                origin: { lat: origin.lat, lon: origin.lon },
                destinations: destinations.map(d => ({ id: d.id, lat: d.lat, lon: d.lon })),
            },
            { withCredentials: true, timeout: 15000 },
        );
        const data = res.data || {};
        for (const dest of destinations) {
            const r = data[dest.id];
            if (r && (r.distance || r.duration)) {
                results[dest.id] = { car_distance: r.distance, car_duration: r.duration };
            }
        }
        if (Object.keys(results).length === destinations.length) return results;
    } catch { /* proxy unavailable, fall through */ }

    // 2) Direct OSRM call (works for most users)
    for (const dest of destinations) {
        if (results[dest.id]) continue;
        try {
            const url = `https://router.project-osrm.org/route/v1/driving/${origin.lon},${origin.lat};${dest.lon},${dest.lat}?overview=false`;
            const ctrl = new AbortController();
            const timer = setTimeout(() => ctrl.abort(), 5000);
            const res = await fetch(url, { signal: ctrl.signal });
            clearTimeout(timer);
            const data = await res.json();
            if (data.code === 'Ok' && data.routes?.length > 0) {
                results[dest.id] = {
                    car_distance: data.routes[0].distance,
                    car_duration: data.routes[0].duration,
                };
            }
        } catch { /* skip */ }
    }

    // 3) Haversine fallback so the user always sees an estimate
    for (const dest of destinations) {
        if (results[dest.id]) continue;
        const straight = haversineMeters(origin, dest);
        const roadEstimate = straight * 1.4;
        const carDurationSec = (roadEstimate / 1000) / 35 * 3600; // 35 km/h average
        results[dest.id] = {
            car_distance: roadEstimate,
            car_duration: carDurationSec,
            estimated: true,
        };
    }
    return results;
}

// Address search with Nominatim
function AddressSearchBar({ onSelect }) {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [searching, setSearching] = useState(false);
    const timeoutRef = useRef(null);
    const wrapperRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(e) {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const search = useCallback(async (q) => {
        if (q.length < 3) { setSuggestions([]); return; }
        setSearching(true);
        try {
            const res = await fetch(`${API_URL}/api/geocode/search?q=${encodeURIComponent(q)}&limit=5`);
            if (!res.ok) { setSuggestions([]); return; }
            const data = await res.json();
            if (!Array.isArray(data)) { setSuggestions([]); return; }
            setSuggestions(data.map(item => ({
                display: item.display_name,
                short: [item.address?.house_number, item.address?.road, item.address?.postcode, item.address?.city || item.address?.town || item.address?.village].filter(Boolean).join(' '),
                city: item.address?.city || item.address?.town || item.address?.village || '',
                postcode: item.address?.postcode || '',
                lat: parseFloat(item.lat),
                lon: parseFloat(item.lon),
            })));
            setShowSuggestions(true);
        } catch { setSuggestions([]); }
        finally { setSearching(false); }
    }, []);

    const handleInput = (e) => {
        const val = e.target.value;
        setQuery(val);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => search(val), 800);
    };

    const handleSelect = (s) => {
        setQuery(s.short || s.display);
        setShowSuggestions(false);
        onSelect(s);
    };

    return (
        <div ref={wrapperRef} className="relative w-full">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                    value={query}
                    onChange={handleInput}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                    placeholder="Rechercher une adresse..."
                    className="pl-11 h-12 text-base bg-white border-gray-200 shadow-sm"
                    data-testid="map-address-search"
                />
                {searching && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="w-5 h-5 border-2 border-[#1B365D] border-t-transparent rounded-full animate-spin" />
                    </div>
                )}
            </div>
            {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-[1000] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                    {suggestions.map((s, i) => (
                        <div key={i} onClick={() => handleSelect(s)}
                            className="px-4 py-3 cursor-pointer hover:bg-[#1B365D]/5 border-b border-gray-50 last:border-0 transition-colors">
                            <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-[#C9A962] shrink-0" />
                                <p className="text-sm font-medium text-[#1B365D]">{s.short}</p>
                            </div>
                            <p className="text-xs text-gray-400 ml-6 truncate">{s.display}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function CarteRDV() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [selectedDay, setSelectedDay] = useState(0); // offset from today
    const [searchedAddress, setSearchedAddress] = useState(null);
    const [events, setEvents] = useState([]);
    const [geocodedEvents, setGeocodedEvents] = useState([]);
    const [routes, setRoutes] = useState({});

    // Auto-set end time = start + 1h
    const autoEndTime = (start) => {
        const [h, m] = start.split(':').map(Number);
        const endH = Math.min(h + 1, 23);
        return `${endH.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    };

    // Visit planning form
    const [visitDate, setVisitDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [visitStartTime, setVisitStartTime] = useState('09:00');
    const [visitEndTime, setVisitEndTime] = useState('10:00');
    const [visitType, setVisitType] = useState('visite_presentiel');

    const handleStartTimeChange = (v) => {
        setVisitStartTime(v);
        setVisitEndTime(autoEndTime(v));
    };

    // Day navigation
    const selectedDate = format(addDays(new Date(), selectedDay), 'yyyy-MM-dd');
    const selectedDateDisplay = selectedDay === 0
        ? "Aujourd'hui"
        : format(addDays(new Date(), selectedDay), 'EEEE d MMMM yyyy', { locale: fr });
    const goNextDay = () => setSelectedDay(prev => Math.min(prev + 1, 30));
    const goPrevDay = () => setSelectedDay(prev => Math.max(prev - 1, -30));
    const goToday = () => setSelectedDay(0);

    // Fetch events for 60-day range
    const fetchEvents = useCallback(async () => {
        try {
            const start = format(addDays(new Date(), -30), 'yyyy-MM-dd');
            const end = format(addDays(new Date(), 30), 'yyyy-MM-dd');
            const res = await axios.get(`${API_URL}/api/events?start=${start}&end=${end}`, { withCredentials: true });
            setEvents(res.data);
        } catch (err) { console.error(err); }
    }, []);

    useEffect(() => { fetchEvents(); }, [fetchEvents]);

    // When day changes, update visit date
    useEffect(() => {
        setVisitDate(selectedDate);
    }, [selectedDate]);

    // Geocode events that have a location - use batch endpoint
    useEffect(() => {
        const eventsForDay = events.filter(ev => ev.date === selectedDate);
        const toGeocode = eventsForDay
            .filter(ev => ev.type === 'visite_presentiel')
            .filter(ev => ev.location || ev.prospect?.adresse_depart?.adresse)
            .map(ev => ({
                event_id: ev.id,
                address: ev.location || ev.prospect?.adresse_depart?.adresse,
            }));

        if (toGeocode.length === 0) {
            setGeocodedEvents([]);
            return;
        }

        const geocode = async () => {
            try {
                const res = await axios.post(`${API_URL}/api/geocode/batch`, {
                    addresses: toGeocode,
                }, { withCredentials: true });
                const geoMap = res.data || {};
                const results = eventsForDay
                    .filter(ev => ev.type === 'visite_presentiel' && geoMap[ev.id])
                    .map(ev => ({ ...ev, ...geoMap[ev.id] }));
                setGeocodedEvents(results);
            } catch {
                setGeocodedEvents([]);
            }
        };
        geocode();
    }, [events, selectedDate]);

    const handleAddressSelect = (addr) => {
        setSearchedAddress(addr);
    };

    // Split events: presentiel on map, virtuelle in side list
    const presentielEvents = useMemo(() => geocodedEvents.filter(ev => ev.type === 'visite_presentiel'), [geocodedEvents]);
    const virtuelleEvents = useMemo(() => events.filter(ev => ev.date === selectedDate && ev.type === 'visite_virtuelle'), [events, selectedDate]);

    // Fetch driving routes from searched address to each presentiel event
    useEffect(() => {
        if (!searchedAddress || presentielEvents.length === 0) {
            setRoutes({});
            return;
        }
        fetchRoutes(searchedAddress, presentielEvents).then(setRoutes);
    }, [searchedAddress, presentielEvents]);

    // All visible map points for auto-fit (only presentiel + searched address)
    const allMapPoints = [
        ...(searchedAddress ? [[searchedAddress.lat, searchedAddress.lon]] : []),
        ...presentielEvents.map(ev => [ev.lat, ev.lon]),
    ];

    const handlePlanifier = () => {
        const params = new URLSearchParams({
            visit_date: visitDate,
            visit_start: visitStartTime,
            visit_end: visitEndTime,
            visit_type: visitType,
        });
        if (searchedAddress) {
            params.set('address', searchedAddress.short || '');
        }
        navigate(`/prospects/nouveau?${params.toString()}`);
    };

    return (
        <Layout>
            <Toaster position="top-right" richColors />
            <div className="h-[calc(100vh-120px)] flex flex-col gap-3" data-testid="carte-rdv-page">
                {/* Top Bar: Search + Visit Form */}
                <div className="flex flex-col lg:flex-row gap-3 flex-shrink-0">
                    {/* Address search */}
                    <div className="flex-1 min-w-0">
                        <AddressSearchBar onSelect={handleAddressSelect} />
                    </div>

                    {/* Visit planning mini form */}
                    <Card className="border border-gray-200 shadow-sm lg:w-auto">
                        <CardContent className="p-3 flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-2">
                                <CalendarIcon className="w-4 h-4 text-[#C9A962]" />
                                <Input
                                    type="date"
                                    value={visitDate}
                                    onChange={(e) => setVisitDate(e.target.value)}
                                    className="h-9 w-[150px] text-sm"
                                    data-testid="plan-date"
                                />
                            </div>
                            <div className="flex items-center gap-1">
                                <Clock className="w-4 h-4 text-[#C9A962]" />
                                <Select value={visitStartTime} onValueChange={handleStartTimeChange}>
                                    <SelectTrigger className="h-9 w-[90px] text-sm" data-testid="plan-start"><SelectValue /></SelectTrigger>
                                    <SelectContent>{HOURS.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}</SelectContent>
                                </Select>
                                <span className="text-gray-400 text-sm">-</span>
                                <Select value={visitEndTime} onValueChange={setVisitEndTime}>
                                    <SelectTrigger className="h-9 w-[90px] text-sm" data-testid="plan-end"><SelectValue /></SelectTrigger>
                                    <SelectContent>{HOURS.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <Select value={visitType} onValueChange={setVisitType}>
                                <SelectTrigger className="h-9 w-[170px] text-sm" data-testid="plan-type">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="visite_presentiel">
                                        <div className="flex items-center gap-2"><Eye className="w-3.5 h-3.5" /> Présentiel</div>
                                    </SelectItem>
                                    <SelectItem value="visite_virtuelle">
                                        <div className="flex items-center gap-2"><Video className="w-3.5 h-3.5" /> Virtuelle</div>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                            <Button
                                onClick={handlePlanifier}
                                className="bg-[#C9A962] hover:bg-[#B8984F] text-white gap-2 h-9 px-5"
                                data-testid="plan-button"
                            >
                                Planifier
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </CardContent>
                    </Card>
                </div>

                {/* Map + Virtual visits sidebar */}
                <div className="flex-1 flex gap-3 min-h-0">
                    {/* Map */}
                    <div className="flex-1 rounded-xl overflow-hidden border border-gray-200 shadow-sm relative z-0">
                        <MapContainer
                            center={IDF_CENTER}
                            zoom={IDF_ZOOM}
                            style={{ height: '100%', width: '100%' }}
                            scrollWheelZoom={true}
                        >
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />

                            {/* Auto-fit map to show all points */}
                            {allMapPoints.length > 0 && <FitBounds points={allMapPoints} />}

                            {/* Red marker: searched address */}
                            {searchedAddress && (
                                <Marker position={[searchedAddress.lat, searchedAddress.lon]} icon={redIcon}>
                                    <Tooltip permanent direction="top" offset={[0, -16]}
                                        className="!bg-red-600 !text-white !border-0 !rounded-lg !px-3 !py-1.5 !shadow-lg !text-xs !font-semibold">
                                        {searchedAddress.short}
                                    </Tooltip>
                                </Marker>
                            )}

                            {/* Blue markers: ONLY presentiel events */}
                            {presentielEvents.map((ev) => {
                            const rd = routes[ev.id];
                            const hasDist = searchedAddress && rd?.car_distance;
                            const transitDur = rd?.car_duration ? Math.round(rd.car_duration * 1.5) : null;
                            return (
                            <Marker key={ev.id} position={[ev.lat, ev.lon]} icon={blueIcon}>
                                <Tooltip permanent direction="top" offset={[0, -14]}
                                    className="!bg-[#1B365D] !text-white !border-0 !rounded-lg !shadow-lg !text-xs !p-0">
                                    <div>
                                        {hasDist && (
                                            <div style={{display:'flex',alignItems:'center',gap:'8px',padding:'4px 10px',background:'rgba(201,169,98,0.15)',borderBottom:'1px solid rgba(255,255,255,0.15)',borderRadius:'8px 8px 0 0',fontSize:'11px',fontWeight:700}}>
                                                <span style={{color:'#C9A962'}}>{formatDistance(rd.car_distance)}</span>
                                                <span style={{opacity:0.5}}>|</span>
                                                <span>&#128663; {formatDuration(rd.car_duration)}</span>
                                                <span style={{opacity:0.5}}>|</span>
                                                <span>&#128652; ~{formatDuration(transitDur)}</span>
                                            </div>
                                        )}
                                        <div style={{padding:'5px 10px'}}>
                                            <div className="font-semibold">{ev.start_time || ''} - {ev.prospect?.nom || ev.title}</div>
                                            <div className="opacity-80">{ev.city}{ev.postcode ? ` ${ev.postcode}` : ''}</div>
                                        </div>
                                    </div>
                                </Tooltip>
                                <Popup>
                                    <div className="text-sm min-w-[180px]">
                                        <p className="font-bold text-[#1B365D]">{ev.prospect?.nom || ev.title}</p>
                                        <p className="text-gray-500">{ev.start_time} - {ev.end_time}</p>
                                        <p className="text-gray-500">{ev.location}</p>
                                        {ev.prospect?.telephone && <p className="text-gray-500">{ev.prospect.telephone}</p>}
                                        <Badge className={`mt-1 ${ev.type === 'visite_presentiel' ? 'bg-[#1B365D]' : 'bg-[#C9A962]'} text-white text-[10px]`}>
                                            {ev.type === 'visite_presentiel' ? 'Présentiel' : 'Virtuelle'}
                                        </Badge>
                                    </div>
                                </Popup>
                            </Marker>
                            );
                        })}

                        {/* Route lines from red marker to each presentiel blue marker */}
                        {searchedAddress && presentielEvents.map((ev) => (
                            <RouteLine
                                key={`route-${ev.id}`}
                                from={[searchedAddress.lat, searchedAddress.lon]}
                                to={[ev.lat, ev.lon]}
                            />
                        ))}
                    </MapContainer>
                </div>

                    {/* Visites virtuelles - Side panel */}
                    <div className="w-[260px] shrink-0 rounded-xl border border-gray-200 bg-white shadow-sm flex flex-col overflow-hidden" data-testid="virtual-visits-panel">
                        <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-[#C9A962]/10 to-transparent">
                            <div className="flex items-center gap-2">
                                <Video className="w-4 h-4 text-[#C9A962]" />
                                <span className="text-sm font-bold text-[#1B365D]">Visites virtuelles</span>
                            </div>
                            <span className="text-[10px] text-gray-400">{virtuelleEvents.length} RDV</span>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {virtuelleEvents.length === 0 ? (
                                <div className="p-4 text-center text-sm text-gray-400">
                                    Aucune visite virtuelle
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-50">
                                    {virtuelleEvents.map((ev) => (
                                        <div key={ev.id} className="px-4 py-3 hover:bg-[#C9A962]/5 transition-colors">
                                            <div className="flex items-center gap-2 mb-1">
                                                <div className="w-2.5 h-2.5 rounded-full bg-[#C9A962] shrink-0" />
                                                <span className="text-xs font-bold text-[#1B365D]">{ev.start_time || ''} - {ev.end_time || ''}</span>
                                            </div>
                                            <p className="text-sm font-semibold text-gray-800 ml-[18px]">{ev.prospect?.nom || ev.title}</p>
                                            {ev.prospect?.telephone && (
                                                <p className="text-xs text-gray-400 ml-[18px]">{ev.prospect.telephone}</p>
                                            )}
                                            {ev.location && (
                                                <p className="text-xs text-gray-400 ml-[18px] truncate">{ev.location}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Day navigation */}
                <div className="flex items-center justify-center gap-3 flex-shrink-0 pb-1" data-testid="day-filters">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={goPrevDay}
                            disabled={selectedDay <= -30}
                            data-testid="day-prev"
                            className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-[#1B365D] hover:bg-[#1B365D] hover:text-white transition-all disabled:opacity-30 disabled:pointer-events-none"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>

                        <div className="text-center min-w-[200px]">
                            <div className="text-lg font-bold text-[#1B365D] capitalize" data-testid="day-display">
                                {selectedDateDisplay}
                            </div>
                            <div className="text-xs text-gray-400">
                                {format(addDays(new Date(), selectedDay), 'dd/MM/yyyy')}
                            </div>
                        </div>

                        <button
                            onClick={goNextDay}
                            disabled={selectedDay >= 30}
                            data-testid="day-next"
                            className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-[#1B365D] hover:bg-[#1B365D] hover:text-white transition-all disabled:opacity-30 disabled:pointer-events-none"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>

                        {selectedDay !== 0 && (
                            <button
                                onClick={goToday}
                                data-testid="day-today"
                                className="px-3 py-1.5 rounded-lg bg-[#C9A962] text-white text-xs font-semibold hover:bg-[#B8984F] transition-colors ml-1"
                            >
                                Aujourd'hui
                            </button>
                        )}
                    </div>

                    {/* Event count */}
                    <div className="flex items-center gap-2 text-sm text-gray-500 shrink-0 ml-4">
                        <div className="w-3 h-3 rounded-full bg-[#1B365D]" />
                        <span>{presentielEvents.length} présentiel</span>
                        <div className="w-3 h-3 rounded-full bg-[#C9A962] ml-2" />
                        <span>{virtuelleEvents.length} virtuelle</span>
                        {searchedAddress && (
                            <>
                                <div className="w-3 h-3 rounded-full bg-red-500 ml-2" />
                                <span>Recherche</span>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
}
